import { ChangeDetectorRef, Component, Input, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { ComponentRegistryService } from '@services/component-registry/component-registry.service';

import { MatSnackBar } from '@angular/material/snack-bar';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-alt-nav',
  templateUrl: './alt-nav.component.html',
  styleUrls: ['./alt-nav.component.scss'],
  host: { class: 'txp-alt-nav' },
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    NgxPermissionsModule,
    MatMenuTrigger,
    MatMenu,
    MatTooltip,
    MatIcon,
    MatMenuItem,
    TranslateModule,
  ],
})
export class AltNavComponent implements OnInit, OnChanges {
  /** navConfig is an array of json:
   *      id name 'icon' is required for icon's class value
   *      id name 'iconRout' is require for destination rout
   *      example:
   *      {'icon': 'iconClass', 'route': 'destinationPage'}
   */
  @Input() navConfig: any[];
  @Input() currentRoute = '/';
  selectedIdx: number;
  isCallCenterModeActive = false;
  userId: string | null = null;

  get brandId(): string {
    return sessionStorage.getItem('brand_id');
  }

  constructor(
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private permissionsService: PermissionsService,
    public router: Router,
    private componentRegistryService: ComponentRegistryService,
  ) {
    // router referenced in template
  }

  ngOnInit() {
    this.authService.user$.subscribe((user) => {
      this.userId = user?.sub || user?.email || null;
    });
  }

  ngOnChanges() {
    if (this.navConfig.length > 0 && this.currentRoute !== '/') {
      this.setSelected();
      this.changeDetectorRef.detectChanges();
    }
  }

  logout() {
    // Save all user-specific orders dashboard filter keys/values
    const filterKeys = [
      'ordersDashboardSortNewestFirst_',
      'ordersDashboardActiveTimerFilter_',
      'ordersDashboardSelectedServiceType_',
      'ordersDashboardStoreFilter_',
      'ordersDashboardNext5Minutes_',
      'ordersDashboardSearchText_',
      'ordersDashboardStoreSearchText_',
      'storeMenuShowImages',
    ];
    const filterValues: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && filterKeys.some((prefix) => key.startsWith(prefix))) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          filterValues.push([key, value]);
        }
      }
    }

    this.authService.logout({
      logoutParams: {
        federated: true,
        returnTo: window.location.origin,
      },
    });
    window.sessionStorage.clear();
    localStorage.clear();

    // Restore all user-specific orders dashboard filter keys/values
    filterValues.forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }

  shouldShow(link) {
    return !link.hide && this.permissionsService.getPermission(link.permission);
  }

  setSelected() {
    if (this.currentRoute) {
      this.selectedIdx = this.navConfig.findIndex((item) =>
        this.currentRoute.startsWith(item.path),
      );
    }
  }

  routeToExternalVersion(url, message) {
    if (url && !!this.brandId) {
      const brand = this.brandId;
      const bearer = localStorage.getItem('bearer');
      const route = `${url}xp?brand=${brand}&auth=${bearer}`;
      return (window.location.href = route);
    } else {
      this.snackBar.open(message, '', { duration: 3000, verticalPosition: 'top' });
    }
  }

  /**
   * Handles navigation click with automatic same-route reload support.
   * If navigating to the same route, checks CanDeactivate guard and adds _reload query parameter to force component reload.
   *
   * @param path - Route path to navigate to
   */
  handleNavigation(path: string): void {
    if (!path) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0]; // Remove query params for comparison
    const targetUrl = path.split('?')[0];
    const isSameRoute = targetUrl === currentUrl;

    if (isSameRoute) {
      // Check CanDeactivate guard before reloading
      this.checkCanDeactivateAndReload(path);
    } else {
      // Navigate to different route normally
      this.router.navigateByUrl(path);
    }
  }

  /**
   * Checks CanDeactivate guard before reloading the route.
   * If guard passes, proceeds with reload. If guard fails (user cancels), no navigation occurs.
   *
   * @param path - Route path to navigate to after guard check
   */
  private async checkCanDeactivateAndReload(path: string): Promise<void> {
    try {
      const canDeactivate = await this.componentRegistryService.checkCanDeactivate();
      if (canDeactivate) {
        // Guard passed, proceed with reload
        this.performReload(path);
      }
      // If guard returns false, do nothing (user cancelled)
    } catch (error) {
      // If guard check fails, proceed with reload anyway (fail open)
      console.error('Error checking CanDeactivate guard:', error);
      this.performReload(path);
    }
  }

  openDocumentation(): void {
    window.open('https://tillsterdocs.tillster.com/', '_blank', 'noopener,noreferrer');
  }

  /**
   * Performs the reload by navigating with _reload query parameter.
   *
   * @param path - Route path to navigate to
   */
  private performReload(path: string): void {
    const separator = this.router.url.includes('?') ? '&' : '?';
    const reloadUrl = `${this.router.url}${separator}_reload=${Date.now()}`;

    this.router.navigateByUrl(reloadUrl).then((reloadNavigated: boolean) => {
      if (reloadNavigated) {
        // Immediately remove the query parameter to clean the URL
        // Use replaceUrl to avoid adding to history
        this.router.navigateByUrl(path, { replaceUrl: true });
      }
    });
  }
}
